import {
    DocumentVisibilityTracker,
    IndexedDBService,
    TabManagerService,
    UUIDService,
} from '@pulse/browser-shared';
import {
    PulseBatchConfig,
    PulseMessage,
    PulseMessagePriority,
    TrackerConfigurations,
} from '@pulse/common';
import {
    BehaviorSubject,
    Subject,
    buffer,
    combineLatest,
    filter,
    from,
    interval,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import { SessionService, TabInfoService, TrackingConsentService } from '../services';
import { getBatches, getTrackerBatchConfig } from './batch-helper';
import { BatchQueue } from './batch-queue';

const EVENT_MAX_AGE_MS = 5 * 24 * 60 * 60 * 1000; // 5 days

type StoredEventRecord = {
    id: string;
    message: PulseMessage;
    time: number;
    tabId?: string;
    sessionId?: string;
};

export class MessageQueue {
    readonly queueEmpty$: Subject<boolean>;
    private trackingEnabled?: boolean;
    private batchConfig: PulseBatchConfig;
    private messageReceived = new Subject<PulseMessage>();
    private flushRequested = new Subject<void>();

    constructor(
        private consentService: TrackingConsentService,
        private config: TrackerConfigurations,
        private batchQueue: BatchQueue,
        private visibility: DocumentVisibilityTracker,
        private readonly indexedDB: IndexedDBService,
        private readonly uuid: UUIDService,
        private readonly tabManager: TabManagerService,
        private readonly sessionService: SessionService,
        private readonly tabInfoService: TabInfoService,
    ) {
        this.queueEmpty$ = batchQueue.queueEmpty$;
        this.batchConfig = getTrackerBatchConfig(this.config, this.trackingEnabled);

        this.consentService.trackingEnabled().subscribe((enabled) => {
            this.trackingEnabled = enabled;
            this.batchConfig = getTrackerBatchConfig(this.config, this.trackingEnabled);
        });

        this.visibility.onVisibilityChange().subscribe((visible) => {
            if (!visible && this.trackingEnabled) {
                this.flush();
            }
        });

        this.start();
        this.initFromIndexedDB();
    }

    /**
     * Emit an event.
     * Event will be pushed into queue and will be scheduled to transmission.
     */
    push(...messages: PulseMessage[]) {
        const force = messages.some((_) => _.priority === PulseMessagePriority.Force);
        const immediate = messages.some((_) => _.priority === PulseMessagePriority.High) || force;

        if (!this.trackingEnabled && !force) {
            return;
        }

        this.persistMessages(messages);
        messages.forEach((message) => this.messageReceived.next(message));
        if ((immediate && this.trackingEnabled) || force) {
            // In case of navigation events for example
            // the event is published right before the tab is closed
            // Hence, using doPublishSync to publish the message immediately
            this.flushRequested.next();
        }

        return;
    }

    /**
     * Signals the messages subscription to immediately compose batches from
     *  the message we currently have in the message queue
     */
    flush(): void {
        this.flushRequested.next();
    }

    /**
     * Starts an observable that listens to events (published via messageReceived subject)
     * And will buffer the events till they reach a certain batchSize, or a time limit expires
     *
     * It will flush all immediately if flushRequested subject emits, then it will re-listen recursively
     * as takeUntil operator will result in the completion and destruction of the original subscription
     */
    private start() {
        const renewInterval$ = new Subject<void>();
        const bufferInterval$ = renewInterval$.pipe(
            startWith({}),
            switchMap(() => interval(this.config.batchTimeMS)),
        );
        const bufferCount$ = new BehaviorSubject<number>(0);
        const bufferCountReached$ = new Subject<void>();
        bufferCount$.pipe(filter((count) => count === this.config.batchSize + 1)).subscribe(() => {
            bufferCountReached$.next();
        });
        const withrowBuffer$ = combineLatest([
            bufferInterval$,
            bufferCountReached$,
            this.flushRequested,
        ]);

        from(this.messageReceived)
            .pipe(
                tap(() => bufferCount$.next(bufferCount$.value + 1)),
                buffer(withrowBuffer$),
            )
            .subscribe((messages) => {
                bufferCount$.next(0);
                renewInterval$.next();
                this.pushToBatchQueue(messages);
            });

        bufferCount$.next(0);
        bufferCountReached$.next();
        this.flushRequested.next();
        renewInterval$.next();
    }

    private pushToBatchQueue(messages: PulseMessage[]): void {
        const batches = getBatches(messages, this.batchConfig);

        if (!batches.length) {
            return;
        }

        batches.map((batch) => this.batchQueue.pushBatch(batch));
    }

    private persistMessages(messages: PulseMessage[]): void {
        if (messages.length === 0) {
            return;
        }

        let tabId: string | undefined;
        let sessionId: string | undefined;
        try {
            tabId = this.tabManager.getTabId();
        } catch {
            // Tab may not be initialized yet
        }
        try {
            sessionId = this.sessionService.getSession(false).sessionId;
        } catch {
            // Session may not be ready
        }

        this.indexedDB
            .runInTransaction(IndexedDBService.EVENTS_STORE, 'readwrite', (tx) => {
                const store = tx.objectStore(IndexedDBService.EVENTS_STORE);
                for (const message of messages) {
                    const id =
                        message.type === 'event' &&
                        (message.data as { event_id?: string })?.event_id
                            ? (message.data as { event_id: string }).event_id
                            : this.uuid.generate();

                    if (
                        (message.type === 'attribute' || message.type === 'attribute-hint') &&
                        typeof message.data === 'object' &&
                        message.data !== null
                    ) {
                        (message.data as { id?: string }).id = id;
                    }

                    store.put({
                        id,
                        message,
                        time: message.timestamp ?? Date.now(),
                        tabId,
                        sessionId,
                    });
                }
            })
            .catch(() => {
                // IndexedDB may be unavailable
            });
    }

    private initFromIndexedDB(): void {
        this.indexedDB
            .getAllValue<StoredEventRecord[]>(IndexedDBService.EVENTS_STORE)
            .then((records) => {
                if (!Array.isArray(records) || records.length === 0) {
                    return;
                }

                const cutoff = Date.now() - EVENT_MAX_AGE_MS;
                const valid = records.filter((r) => (r.time ?? 0) > cutoff);
                const openSiblingTabIds = this.tabInfoService.getOpenSiblingTabIds();
                const ownedByOpenSibling = valid.filter(
                    (r) =>
                        r.tabId !== undefined &&
                        r.tabId !== null &&
                        openSiblingTabIds.includes(r.tabId),
                );
                const toProcess = valid.filter(
                    (r) => !ownedByOpenSibling.some((s) => s.id === r.id),
                );

                const messages = this.recordsToMessages(toProcess);

                if (messages.length > 0) {
                    this.pushToBatchQueue(messages);
                }

                if (toProcess.length > 0) {
                    this.indexedDB
                        .runInTransaction(IndexedDBService.EVENTS_STORE, 'readwrite', (tx) => {
                            const store = tx.objectStore(IndexedDBService.EVENTS_STORE);
                            toProcess.forEach((record) => store.delete(record.id));
                        })
                        .catch(() => undefined);
                }
            })
            .catch(() => undefined);
    }

    private recordsToMessages(records: StoredEventRecord[]): PulseMessage[] {
        const messages: PulseMessage[] = [];

        for (const r of records) {
            if (r === null || r === undefined || r.message === null || r.message === undefined) {
                continue;
            }

            const msg = { ...r.message };
            const hasSessionOrTab =
                (r.sessionId !== undefined && r.sessionId !== null) ||
                (r.tabId !== undefined && r.tabId !== null);
            msg.state = hasSessionOrTab
                ? {
                      ...(msg.state ?? {}),
                      ...(r.sessionId !== undefined &&
                          r.sessionId !== null && {
                              session_id: r.sessionId,
                          }),
                      ...(r.tabId !== undefined &&
                          r.tabId !== null && {
                              tab_id: r.tabId,
                          }),
                  }
                : msg.state;

            messages.push(msg);
        }

        return messages;
    }
}
